/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useEffect, useMemo } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { bind } from 'bind-event-listener';

import DeprecatedButton from '@atlaskit/button';
import type { ButtonProps } from '@atlaskit/button';
import { jsx } from '@atlaskit/css';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import type { Keymap } from '@atlaskit/editor-common/keymaps';
import { token } from '@atlaskit/tokens';

import { Shortcut } from '../Shortcut/Shortcut';

import { isKeyboardEventMatchShortcut } from './isKeyboardEventMatchShortcut';

const buttonAutoFocusRingFix = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus:not(:focus-visible)': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});

const forceFocusRingStyles = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':not(:focus-visible)': {
		outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
		outlineOffset: token('space.025'),
	},
});

interface Props extends Omit<ButtonProps, 'onClick'> {
	buttonAutoFocusRing?: boolean;
	forceFocusRing?: boolean;
	/** The function to be called when the button is clicked. */
	onClick?: () => void;
	/** The keymap object containing the shortcut information. */
	shortcut?: Keymap;
}

/**
 * Component that renders a button with an optional keyboard shortcut.
 *
 * It binds a keydown event listener to the document to trigger the `onClick` function
 * when the `shortcut` is pressed.
 */
export function ButtonWithShortcut({
	shortcut,
	children,
	forceFocusRing,
	buttonAutoFocusRing,
	onClick,
	...rest
}: Props) {
	useEffect(() => {
		const listener = (event: KeyboardEvent) => {
			if (shortcut?.common && isKeyboardEventMatchShortcut(event, shortcut.common)) {
				event.preventDefault();
				event.stopPropagation();
				onClick?.();
			}
		};

		return bind(document, { type: 'keydown', listener });
	}, [onClick, shortcut]);

	const content = useMemo(() => {
		if (!shortcut) {
			return <Fragment>{children}</Fragment>;
		}

		const browser = getBrowserInfo();

		return (
			<Fragment>
				<span aria-keyshortcuts={browser.mac ? shortcut?.mac : shortcut?.windows}>{children}</span>
				<span aria-hidden="true">
					<Shortcut shortcut={shortcut} />
				</span>
			</Fragment>
		);
	}, [shortcut, children]);

	return (
		// Using DeprecatedButton allows css overriding.
		// Ignored via go/ees005
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/enforce-style-prop
		<DeprecatedButton
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			css={[forceFocusRing && forceFocusRingStyles, buttonAutoFocusRing && buttonAutoFocusRingFix]}
			onClick={onClick}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...rest}
		>
			{content}
		</DeprecatedButton>
	);
}
